import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface StepInteraction {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  status: string;
}

interface CreateFlowStepDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: { interactionModuleId: string; label?: string }) => void;
  isSubmitting: boolean;
  error?: string;
  interactionModules: StepInteraction[];
}

export function CreateFlowStepDialog({
  open,
  onCancel,
  onSave,
  isSubmitting,
  error,
  interactionModules,
}: CreateFlowStepDialogProps) {
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [label, setLabel] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedModuleId("");
      setLabel("");
    }
  }, [open]);

  const handleSubmit = () => {
    if (!selectedModuleId) return;

    onSave({
      interactionModuleId: selectedModuleId,
      label: label.trim() || undefined,
    });
  };

  const hasModules = interactionModules.length > 0;
  const canSubmit = selectedModuleId && !isSubmitting;

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={onCancel}
        onPointerDownOutside={onCancel}
      >
        <DialogHeader>
          <DialogTitle>Add step to flow</DialogTitle>
          <DialogDescription>
            Choose a step interaction to add to this flow.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Step Interaction Select */}
          <div className="space-y-1">
            <label className="text-xs font-medium">
              Step interaction <span className="text-destructive">*</span>
            </label>
            {hasModules ? (
              <Select
                value={selectedModuleId}
                onValueChange={setSelectedModuleId}
                disabled={isSubmitting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a step interaction..." />
                </SelectTrigger>
                <SelectContent>
                  {interactionModules.map((mod) => (
                    <SelectItem key={mod._id} value={mod._id}>
                      <div className="flex flex-col items-start">
                        <span className="text-sm">{mod.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {mod.slug}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-3 bg-muted/50 border border-border rounded-md">
                <p className="text-sm text-muted-foreground">
                  No available step interactions for this server yet.
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              The interaction that powers this step.
            </p>
          </div>

          {/* Step Label */}
          <div className="space-y-1">
            <label className="text-xs font-medium">Step label</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Optional custom label for this step"
              className="text-sm"
              disabled={isSubmitting || !hasModules}
            />
            <p className="text-xs text-muted-foreground">
              A friendly name for this step in the flow. Leave blank to use the
              interaction name.
            </p>
          </div>

          {error && (
            <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            size="sm"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            size="sm"
            disabled={!canSubmit || !hasModules}
          >
            {isSubmitting ? "Adding..." : "Add step"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
